import { buildCreatableOptions, comboboxKey, initGameFormCombobox, normalizeComboboxName, parseComboboxNames } from './game-form-combobox.js';

(function () {
    const tagFields = Array.from(document.querySelectorAll('[data-game-form-tags-field]'));
    if (tagFields.length === 0) {
        return;
    }

    const parseTagNames = (rawValue) => {
        const tagNames = [];
        const seen = new Set();

        rawValue
            .split(/[,\n]/)
            .map((part) => normalizeComboboxName(part))
            .forEach((tagName) => {
                if (tagName === '') {
                    return;
                }

                const key = comboboxKey(tagName);
                if (seen.has(key)) {
                    return;
                }

                seen.add(key);
                tagNames.push(tagName);
            });

        return tagNames;
    };

    tagFields.forEach((field) => {
        if (!(field instanceof HTMLElement)) {
            return;
        }

        const tagsValueInput = field.querySelector('[data-game-form-tags-value]');
        const tokenizer = field.querySelector('[data-game-form-tags-tokenizer]');
        const chipList = field.querySelector('[data-game-form-tags-chip-list]');
        const entryInput = field.querySelector('[data-game-form-tags-entry]');
        const toggle = field.querySelector('[data-game-form-tags-toggle]');
        const optionsList = field.querySelector('[data-game-form-tags-options]');

        if (
            !(tagsValueInput instanceof HTMLInputElement)
            || !(tokenizer instanceof HTMLElement)
            || !(chipList instanceof HTMLElement)
            || !(entryInput instanceof HTMLInputElement)
            || !(toggle instanceof HTMLButtonElement)
            || !(optionsList instanceof HTMLUListElement)
        ) {
            return;
        }

        const suggestionNames = parseComboboxNames(field.dataset.gameFormSuggestions ?? '[]');

        let tagNames = parseTagNames(tagsValueInput.value);

        const syncSerializedValue = () => {
            tagsValueInput.value = tagNames.join(', ');
        };

        const renderChips = () => {
            const fragment = document.createDocumentFragment();

            tagNames.forEach((tagName, index) => {
                const chip = document.createElement('span');
                chip.className = 'game-form-tags-chip';

                const chipLabel = document.createElement('span');
                chipLabel.className = 'game-form-tags-chip-label';
                chipLabel.textContent = tagName;

                const removeButton = document.createElement('button');
                removeButton.type = 'button';
                removeButton.className = 'game-form-tags-chip-remove';
                removeButton.setAttribute('aria-label', `Remove tag ${tagName}`);
                removeButton.dataset.gameFormTagRemoveIndex = String(index);
                removeButton.textContent = '×';

                chip.append(chipLabel, removeButton);
                fragment.appendChild(chip);
            });

            chipList.replaceChildren(fragment);
        };

        const addTagNames = (incomingTagNames) => {
            if (incomingTagNames.length === 0) {
                return;
            }

            const existingKeys = new Set(tagNames.map((tagName) => comboboxKey(tagName)));

            incomingTagNames.forEach((tagName) => {
                const key = comboboxKey(tagName);
                if (existingKeys.has(key)) {
                    return;
                }

                existingKeys.add(key);
                tagNames.push(tagName);
            });

            syncSerializedValue();
            renderChips();
        };

        const removeTagByIndex = (index) => {
            if (index < 0 || index >= tagNames.length) {
                return;
            }

            tagNames.splice(index, 1);
            syncSerializedValue();
            renderChips();
        };

        const commitEntryValue = () => {
            const parsedNames = parseTagNames(entryInput.value);
            entryInput.value = '';
            addTagNames(parsedNames);
        };

        chipList.addEventListener('click', (event) => {
            if (!(event.target instanceof Element)) {
                return;
            }

            const removeButton = event.target.closest('[data-game-form-tag-remove-index]');
            if (!(removeButton instanceof HTMLButtonElement)) {
                return;
            }

            const removeIndex = Number.parseInt(removeButton.dataset.gameFormTagRemoveIndex ?? '', 10);
            if (Number.isNaN(removeIndex)) {
                return;
            }

            removeTagByIndex(removeIndex);
            entryInput.focus();
        });

        // Registered before the tag keydown below, so an open list consumes Enter first.
        initGameFormCombobox({
            root: tokenizer,
            input: entryInput,
            toggle,
            list: optionsList,
            buildOptions: (query) => buildCreatableOptions({
                query,
                names: suggestionNames,
                chosen: tagNames,
                emptyLabel: 'No more tags to add.',
                takenLabel: 'Tag already added.',
            }),
            onSelect: (option) => {
                entryInput.value = '';
                if (option.type === 'create') {
                    suggestionNames.push(option.value);
                }
                addTagNames([option.value]);
            },
        });

        entryInput.addEventListener('keydown', (event) => {
            if (event.defaultPrevented) {
                return;
            }

            if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                commitEntryValue();
                return;
            }

            if (event.key === 'Backspace' && entryInput.value === '' && tagNames.length > 0) {
                event.preventDefault();
                removeTagByIndex(tagNames.length - 1);
            }
        });

        // Commit only when focus leaves the whole tokenizer: moving to its toggle or a chip's remove
        // button must not turn a half-typed fragment into a tag (or re-render the clicked chip away).
        tokenizer.addEventListener('focusout', (event) => {
            if (event.relatedTarget instanceof Node && tokenizer.contains(event.relatedTarget)) {
                return;
            }
            commitEntryValue();
        });

        entryInput.addEventListener('paste', (event) => {
            const clipboard = event.clipboardData?.getData('text') ?? '';
            if (!/[,\n]/.test(clipboard)) {
                return;
            }

            event.preventDefault();
            addTagNames(parseTagNames(clipboard));
            entryInput.value = '';
        });

        tagsValueInput.type = 'hidden';
        tokenizer.hidden = false;

        syncSerializedValue();
        renderChips();
    });
})();
